import React, { useState, useEffect, useRef } from 'react';
import styled, { ThemeProvider, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import BirthdayMessage from './components/BirthdayMessage';
import ImageGallery from './components/RomanticSurprise';
import WishForm from './components/WishForm';
import ConfettiCannon from './components/ConfettiCannon';
import AnimatedWish from './components/AnimatedWish';
import HeartAnimation from './components/HeartAnimation';
import HeartTrail from './HeartTrail';
import RomanticMusic from './RomanticMusic';
import Lottie from 'react-lottie';

const theme = {
    primaryColor: '#e91e63',
    secondaryColor: '#fce4ec',
    textColor: '#333',
    fontFamily: 'Dancing Script, cursive',
};

const pulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.03); }
    100% { transform: scale(1); }
`;

const floatingHearts = keyframes`
    0% { transform: translateY(0) rotate(-45deg) scale(0); opacity: 0; }
    50% { opacity: 1; transform: translateY(50vh) rotate(-45deg) scale(1); }
    100% { transform: translateY(100vh) rotate(-45deg) scale(0.8); opacity: 0; }
`;

const HeartRainContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
`;

const FallingHeart = styled.div`
    color: ${props => props.theme.primaryColor};
    font-size: 1.5em;
    position: absolute;
    transform: rotate(-45deg);
    transform-origin: top left;
    animation: ${floatingHearts} ${props => props.duration}s linear infinite;
    left: ${props => props.left}%;
    opacity: ${props => props.opacity};
`;

const Background = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
    background: linear-gradient(to bottom, #fce4ec 0%, #f8bbd0 100%);
    z-index: -1;
`;

const AppContainer = styled(motion.div)`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: ${props => props.isLoading ? 'center' : 'flex-start'};
    min-height: 100vh;
    padding: 20px; /* Reduced padding for smaller screens */
    font-family: ${(props) => props.theme.fontFamily};
    z-index: 1;
    box-sizing: border-box; /* Ensure padding doesn't add to width */
`;

const Title = styled(motion.h1)`
    color: ${(props) => props.theme.primaryColor};
    margin-bottom: 30px; /* Reduced margin for smaller screens */
    text-align: center;
    font-size: 2.5em; /* Responsive font size */
    text-shadow: 2px 2px #f48fb1;
    cursor: pointer;
    /* Add media query for even smaller screens if needed */
    @media (max-width: 320px) {
        font-size: 2em;
    }
`;

const SecretWishContainer = styled(motion.div)`
    margin-top: 30px; /* Reduced margin for smaller screens */
    padding: 15px; /* Reduced padding for smaller screens */
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    text-align: center;
    font-size: 1em;
    color: ${(props) => props.theme.primaryColor};
    font-weight: bold;
    opacity: 0;
    transform: scale(0.8);
    width: 90%; /* Make it responsive */
    max-width: 400px; /* Limit max width */
`;

const generateRandom = (min, max) => Math.random() * (max - min) + min;

const InitialEmojisContainer = styled(motion.div)`
    display: flex;
    align-items: center;
    gap: 15px; /* Reduced gap for smaller screens */
    margin-bottom: 30px; /* Reduced margin for smaller screens */
    /* Make it responsive */
    width: 100%;
    justify-content: center;
`;

const VoiceMessageContainer = styled(motion.div)`
    margin-top: 20px; /* Reduced margin for smaller screens */
    text-align: center;
`;

const VoiceMessageButton = styled.button`
    background-color: #ffc0cb;
    color: white;
    border: none;
    padding: 10px 20px; /* Reduced padding for smaller screens */
    border-radius: 20px;
    cursor: pointer;
    font-size: 1em;
    outline: none;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #ffb6c1;
    }
`;

const PageWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 90%; /* Make it responsive */
    max-width: 80vh; /* But limit the max width */
    height: auto; /* Adjust height, it was fixed and causing issues on small screens */
    min-height: 60vh; /* Ensure a minimum height */
    background: radial-gradient(#fce4ec, #f8bbd0);
    border-radius: 10px; /* Add border radius for better look */
    margin-bottom: 20px; /* Add some margin below PageWrapper */
    box-sizing: border-box;
`;

const LoadingImage = styled.img`
    width: 80%; /* Make it responsive */
    max-width: 400px; /* Limit max width */
    height: auto;
    margin-bottom: 20px;
    animation: ${pulse} 2s infinite;
    background: transparent;
    mix-blend-mode: multiply;
`;

function App() {
    const [wishes, setWishes] = useState([]);
    const [showConfetti, setShowConfetti] = useState(false);
    const [secretWishVisible, setSecretWishVisible] = useState(false);
    const [clickCount, setClickCount] = useState(0);
    const [showInitialEmojis, setShowInitialEmojis] = useState(true);
    const voiceMessageURL = "URL_TO_YOUR_VOICE_MESSAGE.mp3";
    const [isLoading, setIsLoading] = useState(true);
    const lottieOptions = useRef({
        loop: true,
        autoplay: true,
        animationData: null,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice',
        },
    });
    const [lottieJson, setLottieJson] = useState(null);

    useEffect(() => {
        const timer1 = setTimeout(() => {
            setShowInitialEmojis(false);
        }, 3000);

        const timer2 = setTimeout(() => {
            setIsLoading(false);
        }, 2000);

        fetch('https://lottie.host/l/YOUR_LOTTIE_ANIMATION_ID')
            .then(response => response.json())
            .then(data => {
                setLottieJson(data);
                lottieOptions.current.animationData = data;
            })
            .catch(error => console.error("Error loading Lottie:", error));

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
        };
    }, []); // Empty dependency array is now safe as lottieOptions is a ref

    const addWish = (name, message) => {
        setWishes([...wishes, { name, message }]);
        setShowConfetti(true);
        setTimeout(() => {
            setShowConfetti(false);
        }, 5000);
    };

    const handleTitleClick = () => {
        setClickCount(clickCount + 1);
        if (clickCount >= 5 && !secretWishVisible) {
            setSecretWishVisible(true);
        }
    };

    const heartRainCount = 50;
    const hearts = Array.from({ length: heartRainCount }, (_, index) => ({
        id: index,
        left: generateRandom(0, 100),
        duration: generateRandom(5, 15),
        opacity: generateRandom(0.5, 1),
        delay: generateRandom(0, 5),
    }));

    const images = [
        'https://via.placeholder.com/400/ffb6c1/ffffff?Text=Love1',
        'https://via.placeholder.com/400/f08080/ffffff?Text=Memory2',
        'https://via.placeholder.com/400/e6e6fa/778899?Text=Us3',
        'https://via.placeholder.com/400/dda0dd/ffffff?Text=Special4',
        'https://via.placeholder.com/400/ffc0cb/000000?Text=Sweet5',
    ];

    return (
        <ThemeProvider theme={theme}>
            <Background />
            <HeartRainContainer>
                {hearts.map(heart => (
                    <FallingHeart
                        key={heart.id}
                        style={{ animationDelay: `${heart.delay}s` }}
                        left={heart.left}
                        duration={heart.duration}
                        opacity={heart.opacity}
                    >
                        ❤️
                    </FallingHeart>
                ))}
            </HeartRainContainer>
            <HeartTrail />
            <AppContainer isLoading={isLoading}>
                {isLoading ? (
                    <>
                        <LoadingImage src="/micky.png" alt="Loading..." />
                    </>
                ) : (
                    <>
                        {showInitialEmojis && lottieJson && (
                            <InitialEmojisContainer
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.8, ease: 'easeInOut' }}
                            >
                                <Lottie options={lottieOptions.current} height={100} width={100} />
                            </InitialEmojisContainer>
                        )}
                        <HeartAnimation size="30px" top="20px" left="20px" /> {/* Adjusted size and position */}
                        <Title
                            initial={{ scale: 0, opacity: 0, rotate: -180 }}
                            animate={{ scale: 1, opacity: 1, rotate: 0 }}
                            transition={{ type: 'spring', stiffness: 100, damping: 10, duration: 1 }}
                            onClick={handleTitleClick}
                        >
                            Happy Birthday, My Beloved!
                        </Title>
                        <PageWrapper>
                            <BirthdayMessage recipient="Special One" />
                        </PageWrapper>
                        <ImageGallery images={images} />
                        <WishForm onWishSubmit={addWish} />
                        {wishes.length > 0 && (
                            <motion.div
                                initial={{ y: 50, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.7, delay: 0.3, ease: 'easeInOut' }}
                                style={{ marginTop: '30px', width: '90%', maxWidth: '600px' }} /* Adjusted marginTop and width */
                            >
                                <h2>With Love from Everyone:</h2>
                                {wishes.map((wish, index) => (
                                    <AnimatedWish key={index} id={index} name={wish.name} message={wish.message} animationDelay={index * 0.2} />
                                ))}
                            </motion.div>
                        )}
                        {secretWishVisible && (
                            <SecretWishContainer
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, ease: 'easeInOut' }}
                            >
                                A secret wish just for you: You make every day feel like a celebration. ✨
                            </SecretWishContainer>
                        )}
                        {showConfetti && <ConfettiCannon />}
                        <VoiceMessageContainer
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 1.5, ease: 'easeInOut' }}
                        >
                            <VoiceMessageButton onClick={() => window.open(voiceMessageURL, '_blank')}>
                                Listen to a Special Message 🎧
                            </VoiceMessageButton>
                        </VoiceMessageContainer>
                        <RomanticMusic audioSource="/happy-birthday-240348.mp3" />
                    </>
                )}
            </AppContainer>
        </ThemeProvider>
    );
}

export default App;